import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

let toastIdCounter = 0;

const TOAST_CONFIG = {
  success: { icon: CheckCircle, bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-800', iconColor: 'text-emerald-500' },
  error: { icon: XCircle, bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', iconColor: 'text-red-500' },
  warning: { icon: AlertTriangle, bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', iconColor: 'text-amber-500' },
  info: { icon: Info, bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', iconColor: 'text-blue-500' },
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastIdCounter;
    setToasts(prev => [...prev.slice(-4), { id, message, type }]);
    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => removeToast(id), duration);
    }
    return id;
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none" aria-live="polite">
        {toasts.map(toast => {
          const config = TOAST_CONFIG[toast.type] || TOAST_CONFIG.info;
          const IconComp = config.icon;
          return (
            <div
              key={toast.id}
              className={`pointer-events-auto flex items-start gap-2.5 rounded-lg border ${config.border} ${config.bg} px-4 py-3 shadow-lg min-w-[280px] max-w-[420px] animate-[slideInRight_0.3s_ease-out]`}
              role="status"
            >
              <IconComp size={18} className={`${config.iconColor} flex-shrink-0 mt-0.5`} />
              <p className={`text-sm font-medium ${config.text} flex-1`}>{toast.message}</p>
              <button
                onClick={() => removeToast(toast.id)}
                className="flex-shrink-0 rounded p-0.5 hover:bg-black/5 transition-colors"
                aria-label="Dismiss notification"
              >
                <X size={14} className="text-slate-400" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('useToast called outside ToastProvider — falling back to alert()');
    }
    return {
      showToast: (msg) => { window.alert(msg); },
      removeToast: () => {},
    };
  }
  return context;
};

export default ToastProvider;
